import React, {
  useCallback,
  useState,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {
  Divider,
  Collapse,
  Hidden,
  BottomNavigation,
  BottomNavigationAction,
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { useHistory, useLocation } from 'react-router-dom';
import { FaHome, FaClipboardList } from 'react-icons/fa';
import { useSelector, useDispatch } from 'react-redux';
import { addDays, startOfDay, subDays } from 'date-fns';
import { ToastContainer } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/auth';
import { useWindowSize } from '../../hooks/windowSize';
import { usePreferences } from '../../hooks/preferences';
import { faLogEvent } from '../../services/firebase/analytics';

import Basemap, { INITIAL_SELECTED } from '../../components/maps/Basemap';
import InfoCard from '../../components/cards/InfoCard';
import PieChart from '../../components/charts/PieChart';
import CountOrdersChart from '../../components/charts/CountOrdersChart';
import CreateOrderButton from '../../components/buttons/CreateOrderButton';
import InfoCardWithDropdown from '../../components/cards/InfoCardWithDropdown';
import ToggleBottomBar from '../../components/nav/ToggleBottomBar';
import ToggleRightBar from '../../components/nav/ToggleRightBar';
import CustomDropdown from '../../components/selectors/CustomDropdown';
import DatePicker from '../../components/selectors/DatePicker';
import MobileDashboard from '../../components/layouts/MobileDashboard';
import BottomBar from '../../components/layouts/BottomBar';
import RightBar from '../../components/layouts/RightBar';
import IncompleteOrdersContent from './IncompleteOrdersContent';
import IncompleteOrdersContentMobile from './IncompleteOrdersContentMobile';

import {
  calculateExecutionRate,
  calculateOrdersAging,
  countByDateByPriority,
} from '../../utils/getChartsValues';
import { getOrderPriorityTranslation } from '../../utils/helper';

import { IOrdersState } from '../../store/modules/orders/types';
import { getOrdersRequest } from '../../store/modules/orders/actions';
import { IState } from '../../store';
import { IOrderDetailed } from '../../types/orders';
import { ISelectedFeature } from '../../types/layers';
import { useStyles } from './styles';

interface IReasons {
  interval: number;
  anotherPlaceRequest: number;
  eletricCableLoading: number;
  shiftEnded: number;
}

interface ISerie {
  name: string;
  data: number[];
}

interface ILocationState {
  shouldCreateOrder?: boolean;
}

const Orders: React.FC = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();
  const history = useHistory();
  const { user, jwt, selectedUnit, selectUnit } = useAuth();
  const { disableTabs, selectDataType } = usePreferences();
  const size = useWindowSize();
  const dispatch = useDispatch();
  const { state } = useLocation<ILocationState>();
  const mapRef = useRef<any>();
  const [selectedFeature, setSelectedFeature] = useState<ISelectedFeature>(
    INITIAL_SELECTED,
  );
  const [openUnitSelector, setOpenUnitSelector] = useState(false);
  const orders = useSelector<IState, IOrdersState>(appState => appState.orders);
  const [openOrders, setOpenOrders] = useState<IOrderDetailed[]>([]);
  const [finishedOrders, setFinishedOrders] = useState<IOrderDetailed[]>([]);
  const [unitOpenOrders, setUnitOpenOrders] = useState<IOrderDetailed[]>([]);
  const [unitFinishedOrders, setUnitFinishedOrders] = useState<
    IOrderDetailed[]
  >([]);
  const [filteredOrders, setFilteredOrders] = useState<IOrderDetailed[]>([]);
  const [chartsFilteredOrders, setChartsFilteredOrders] = useState<
    IOrderDetailed[]
  >([]);
  const [isRightBarOpen, setIsRightBarOpen] = useState(true);
  const [isBottomBarOpen, setIsBottomBarOpen] = useState(true);
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [
    showMobileOpenOrdersContent,
    setShowMobileOpenOrdersContent,
  ] = useState(false);
  const [countIncompleteOrders, setCountIncompleteOrders] = useState<IReasons>({
    interval: 0,
    anotherPlaceRequest: 0,
    eletricCableLoading: 0,
    shiftEnded: 0,
  });
  const [selectedPriority, setSelectedPriority] = useState<string | null>(null);
  const [ordersHistoryData, setOrdersHistoryData] = useState<ISerie[]>([]);
  const [ordersHistoryLabels, setOrdershistoryLabels] = useState<number[]>([]);
  const [loadingChart, setLoadingChart] = useState(true);
  const [agingSeries, setAgingSeries] = useState<ISerie[]>([]);
  const [agingLabels, setAgingLabels] = useState<number[]>([]);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [timescale, setTimescale] = useState(t('components:selectors.monthly'));
  const [openTimescale, setOpenTimescale] = useState(false);
  const [selectedBottomData, setSelectedBottomData] = useState<string>(
    'history',
  );
  const [executionRateData, setExecutionRateData] = useState<ISerie[]>([]);
  const [executionRateLabels, setExecutionRateLabels] = useState<number[]>([]);

  const getOrders = useCallback(() => {
    dispatch(
      getOrdersRequest({
        user,
        jwt,
        status: 'ABERTA,INCOMPLETA,FINALIZADA',
      }),
    );
  }, [user, jwt, dispatch]);

  const getSelectedUnitData = useCallback(() => {
    if (selectedUnit.id === 'itabira') {
      setUnitOpenOrders(openOrders);
      setUnitFinishedOrders(finishedOrders);
    } else {
      const unitData = user.units.find(unit => unit.name === selectedUnit.name);
      if (unitData) {
        setUnitOpenOrders(
          openOrders.filter(order => order.unit_id === unitData.id),
        );
        setUnitFinishedOrders(
          finishedOrders.filter(order => order.unit_id === unitData.id),
        );
      }
    }
  }, [selectedUnit, openOrders, finishedOrders, user.units]);

  const handleCountIncompleteOrders = useCallback(() => {
    const totalIncompleteOrders = filteredOrders.filter(
      order => order.status === 'INCOMPLETA',
    ).length;

    const percentInterval =
      filteredOrders.filter(
        order => order.reason?.toLowerCase() === 'intervalo',
      ).length / totalIncompleteOrders;

    const percentAnotherPlaceRequest =
      filteredOrders.filter(
        order =>
          order.reason?.toLowerCase() === 'demanda urgente em outro local',
      ).length / totalIncompleteOrders;

    const percentEletricCableLoading =
      filteredOrders.filter(
        order =>
          order.reason?.toLowerCase() === 'carregamento de cabo elétrico',
      ).length / totalIncompleteOrders;

    const percentShiftEnded =
      filteredOrders.filter(
        order => order.reason?.toLowerCase() === 'final do turno',
      ).length / totalIncompleteOrders;

    setCountIncompleteOrders({
      interval: percentInterval
        ? Number((percentInterval * 100).toFixed(0))
        : 0,
      anotherPlaceRequest: percentAnotherPlaceRequest
        ? Number((percentAnotherPlaceRequest * 100).toFixed(0))
        : 0,
      eletricCableLoading: percentEletricCableLoading
        ? Number((percentEletricCableLoading * 100).toFixed(0))
        : 0,
      shiftEnded: percentShiftEnded
        ? Number((percentShiftEnded * 100).toFixed(0))
        : 0,
    });
  }, [filteredOrders]);

  const handleClearSelectedFeature = useCallback(() => {
    mapRef.current.clearFeatures();
  }, []);

  const handleMobileNavigation = (
    screen: 'open-orders' | 'incomplete-orders',
  ): void => {
    if (screen === 'open-orders') {
      setShowMobileOpenOrdersContent(!showMobileOpenOrdersContent);
    } else {
      setShowMobileOpenOrdersContent(false);
    }
  };

  const handleCountOrdersByPriority = useCallback((): void => {
    if (startDate && endDate) {
      const result = countByDateByPriority(filteredOrders, startDate, endDate);
      setOrdersHistoryData(result.series);
      setOrdershistoryLabels(result.chartDates);
      setLoadingChart(false);
    } else {
      const sortedOrders = filteredOrders.sort((a, b) => {
        return Number(b.code) - Number(a.code);
      });
      const lastDay = new Date(sortedOrders[0].timestamp);
      const firstDay = subDays(lastDay, 7);
      const result = countByDateByPriority(filteredOrders, firstDay, lastDay);
      setOrdersHistoryData(result.series);
      setOrdershistoryLabels(result.chartDates);
      setLoadingChart(false);
    }
  }, [filteredOrders, startDate, endDate]);

  const handleCalculateAging = useCallback(() => {
    const result = calculateOrdersAging(filteredOrders);
    setAgingSeries(result.agingData);
    setAgingLabels(result.agingLabels);
  }, [filteredOrders]);

  const handleCalculateExecutionRate = useCallback((): void => {
    if (startDate && endDate) {
      const result = calculateExecutionRate(
        unitOpenOrders,
        unitFinishedOrders,
        startDate,
        endDate,
      );
      setExecutionRateData(result.executionRateData);
      setExecutionRateLabels(result.weeks);
    } else {
      const sortedOrders = openOrders.sort((a, b) => {
        return Number(b.code) - Number(a.code);
      });
      const lastDay = addDays(
        startOfDay(new Date(sortedOrders[0].timestamp)),
        14,
      );
      const firstDay = subDays(lastDay, 28);
      const result = calculateExecutionRate(
        unitOpenOrders,
        unitFinishedOrders,
        firstDay,
        lastDay,
      );
      setExecutionRateData(result.executionRateData);
      setExecutionRateLabels(result.weeks);
    }
  }, [openOrders, unitOpenOrders, unitFinishedOrders, startDate, endDate]);

  const ordersAmount = useMemo(() => {
    const values = [];

    const totalUrgent = filteredOrders.filter(pnt => pnt.priority === 'URGENTE')
      .length;
    const totalHigh = filteredOrders.filter(order => order.priority === 'ALTA')
      .length;
    const totalAverage = filteredOrders.filter(
      order => order.priority === 'MEDIA',
    ).length;
    const totalLow = filteredOrders.filter(order => order.priority === 'BAIXA')
      .length;

    values.push(totalLow);
    values.push(totalAverage);
    values.push(totalHigh);
    values.push(totalUrgent);

    return values;
  }, [filteredOrders]);

  const handleToggleCreatingOrder = useCallback(() => {
    isCreatingOrder
      ? faLogEvent('CANCEL_ORDER_CREATION')
      : faLogEvent('CREATE_ORDER_FROM_ORDER_SCREEN');
    setIsCreatingOrder(!isCreatingOrder);
  }, [isCreatingOrder]);

  const handleDoneCreatingOrder = useCallback(() => {
    setIsCreatingOrder(false);
    getOrders();
  }, [getOrders]);

  const handleSelectUnit = useCallback(
    (unitName: string) => {
      if (unitName.toLowerCase() === 'complexo de itabira') {
        selectUnit('itabira');
        return;
      }
      const selected = user.units.find(unit => unit.name === unitName);
      if (selected) {
        selectUnit(selected.id);
      }
    },
    [selectUnit, user.units],
  );

  const handleClearDates = (): void => {
    setStartDate(null);
    setEndDate(null);
  };

  const handleOpenBottomBar = useCallback((value: number) => {
    if (value === 0) {
      setSelectedBottomData('history');
    } else if (value === 1) {
      setSelectedBottomData('execution-rate');
    } else if (value === 2) {
      setSelectedBottomData('incomplete-orders');
    }
    setIsBottomBarOpen(true);
  }, []);

  // GET ORDERS FROM STATE
  useEffect(() => {
    if (orders) {
      const data = [...orders.openOrders, ...orders.incompleteOrders];
      setOpenOrders(data);
    }

    if (orders.finishedOrders.length > 0) {
      setFinishedOrders(orders.finishedOrders);
    }
  }, [orders]);

  useEffect(() => {
    if (selectedFeature.name !== '') {
      if (selectedFeature.type === 'fill') {
        const select = unitOpenOrders.filter(
          order => order.region === selectedFeature.name,
        );
        setFilteredOrders(select);
      } else if (selectedFeature.type === 'line') {
        const select = unitOpenOrders.filter(
          order => String(order.road) === String(selectedFeature.name),
        );
        setFilteredOrders(select);
      }
    } else {
      setFilteredOrders(unitOpenOrders);
    }
  }, [selectedFeature, unitOpenOrders]);

  useEffect(() => {
    if (filteredOrders.length > 0) {
      handleCountIncompleteOrders();
    }
  }, [filteredOrders, handleCountIncompleteOrders]);

  useEffect(() => {
    if (unitOpenOrders.length > 0 && unitFinishedOrders.length > 0) {
      handleCalculateExecutionRate();
    }
  }, [unitOpenOrders, unitFinishedOrders, handleCalculateExecutionRate]);

  useEffect(() => {
    if (selectedPriority === null) {
      setChartsFilteredOrders([]);
    } else {
      const filteredData = filteredOrders.filter(order => {
        const translatedStatus: any = getOrderPriorityTranslation(
          order.priority,
        );
        return (
          t(translatedStatus).toLowerCase() === selectedPriority.toLowerCase()
        );
      });
      setChartsFilteredOrders(filteredData);
    }
  }, [selectedPriority, filteredOrders, t]);

  useEffect(() => {
    if (filteredOrders.length > 0) {
      handleCountOrdersByPriority();
      handleCalculateAging();
    }
  }, [handleCountOrdersByPriority, handleCalculateAging, filteredOrders]);

  useEffect(() => {
    if (state && state.shouldCreateOrder === true) {
      setIsCreatingOrder(true);
      setIsBottomBarOpen(false);
      setIsRightBarOpen(false);
      state.shouldCreateOrder = false;
    }
  }, [state]);

  useEffect(() => {
    getSelectedUnitData();
  }, [getSelectedUnitData]);

  // HANDLE ASSETS TAB BAR INITIAL VALUES
  useEffect(() => {
    selectDataType('roads');
    disableTabs({
      roads: false,
      trucks: true,
      drills: true,
    });
  }, [selectDataType, disableTabs]);

  return (
    <div className={classes.root}>
      {/* Basemap */}
      <Basemap
        ref={mapRef}
        isCreatingOrder={isCreatingOrder}
        orders={
          chartsFilteredOrders.length > 0
            ? chartsFilteredOrders
            : unitOpenOrders
        }
        onDoneCreateOrder={() => handleDoneCreatingOrder()}
        selectedFeatureName={select => setSelectedFeature(select)}
      />

      {/* Create order button */}
      <div className={classes.createOrderButton}>
        <CreateOrderButton
          height={48}
          isCreatingOrder={isCreatingOrder}
          onClick={() => handleToggleCreatingOrder()}
        />
      </div>

      {/* Right bar content: open orders info and charts */}
      <Hidden smDown>
        {/* Right side tool bar */}
        {!isRightBarOpen && (
          <ToggleRightBar
            isCreatingOrder={isCreatingOrder}
            selectedFeatureName={selectedFeature.name}
            onClickOpen={() => setIsRightBarOpen(true)}
            onClickClear={handleClearSelectedFeature}
          />
        )}

        {/* Bottom tool bar */}
        {!isBottomBarOpen && (
          <ToggleBottomBar
            isCreatingOrder={isCreatingOrder}
            buttons={[
              t('screens:orders.history'),
              t('screens:orders.executionRate'),
              t('screens:orders.incompleteOrders'),
            ]}
            onClickOpen={() => setIsBottomBarOpen(true)}
            onClickTab={(value: number) => handleOpenBottomBar(value)}
          />
        )}

        <Collapse in={isRightBarOpen}>
          <RightBar onCloseRightBar={() => setIsRightBarOpen(false)}>
            <InfoCardWithDropdown
              title={t('screens:orders.identification')}
              open={openUnitSelector}
              text={selectedUnit.name}
              labels={[
                'Complexo de Itabira',
                ...user.units.map(unit => unit.name),
              ]}
              labelFontsize={18}
              toggle={() => setOpenUnitSelector(!openUnitSelector)}
              onClickItem={(event: any) =>
                handleSelectUnit(event.target.innerText)
              }
            />
            <div className={classes.datePickerContainer}>
              <DatePicker
                id="availability-picker"
                startDate={startDate}
                endDate={endDate}
                onChangeStartDate={(date: Date | null) => setStartDate(date)}
                onChangeEndDate={(date: Date | null) => setEndDate(date)}
                onClickClearDate={handleClearDates}
                marginBottom={0}
                fontSize={10}
                marginTop={8}
              />
              <CustomDropdown
                open={openTimescale}
                text={timescale}
                showBorder
                labels={[
                  t('components:selectors.monthly'),
                  t('components:selectors.weekly'),
                  t('components:selectors.yearly'),
                ]}
                toggle={() => setOpenTimescale(!openTimescale)}
                onClickItem={(event: any) =>
                  setTimescale(event.target.innerText)
                }
                labelFontsize={10}
                paddingHorizontal={8}
                iconSize={16}
                marginTop={8}
              />
            </div>
            <Divider />
            <InfoCard
              title={t('screens:orders.openOrders')}
              content={filteredOrders.length || '0'}
            />
            <Divider />
            <PieChart
              values={ordersAmount}
              title={t('screens:orders.priorityOfOrders')}
              selectedData={(priority: any) => setSelectedPriority(priority)}
              showLegend={false}
            />
            <Divider />
            <CountOrdersChart
              showTitle
              title={t('screens:orders.ordersAging')}
              labels={agingLabels}
              data={agingSeries}
              type="aging"
              xAxisTitle={t('screens:orders.openTime')}
              yAxisTitle={t('screens:orders.numberOfOrders')}
              // height={size.width ? size.height * 0.215 : '100%'}
              // marginBottom={16}
            />
          </RightBar>
        </Collapse>

        {/* Bottom Bar Content */}
        <Collapse in={isBottomBarOpen} timeout={0}>
          <BottomBar
            onCloseBottomBar={() => setIsBottomBarOpen(false)}
            selected={selectedBottomData}
            onSelect={select => setSelectedBottomData(select)}
            bottomButtonsLabels={[
              { label: t('screens:orders.history'), value: 'history' },
              {
                label: t('screens:orders.executionRate'),
                value: 'execution-rate',
              },
              {
                label: t('screens:orders.incompleteOrders'),
                value: 'incomplete-orders',
              },
            ]}
            bottomBarComponents={[
              <CountOrdersChart
                title={t('screens:orders.historyGeneratedOfOrders')}
                labels={ordersHistoryLabels}
                data={ordersHistoryData}
                type="count"
                loading={loadingChart}
                height={size.height ? size.height * 0.2 : '100%'}
                yAxisTitle={t('screens:orders.numberOfOrders')}
              />,
              <CountOrdersChart
                title={t('screens:orders.executionRate')}
                labels={executionRateLabels}
                data={executionRateData}
                type="count"
                height={size.height && size.height * 0.215}
                showLegend
                legendPosition="right"
                legendFontSize="12%"
                yAxisTitle={t('screens:orders.numberOfOrders')}
                colors={[
                  theme.palette.primary.main,
                  theme.palette.primary.light,
                  '#CFD8DC',
                ]}
              />,
              <IncompleteOrdersContent orders={filteredOrders} />,
            ]}
          />
        </Collapse>
      </Hidden>

      {/* Bottom tab bar, only on xs and sm screens */}
      <Hidden mdUp>
        <BottomNavigation showLabels className={classes.bottomNavigation}>
          <BottomNavigationAction
            className={classes.bottomNavLabel}
            label={t('screens:orders.home')}
            onClick={() => history.push('/dashboard/monitoramento')}
            icon={<FaHome size={24} color={theme.palette.common.white} />}
          />
          <BottomNavigationAction
            className={classes.bottomNavLabel}
            label={t('screens:orders.orders')}
            onClick={() => handleMobileNavigation('open-orders')}
            icon={
              <FaClipboardList size={24} color={theme.palette.common.white} />
            }
            style={{
              borderStyle: 'solid',
              borderLeftWidth: 1,
              borderColor: '#FFF',
            }}
          />
        </BottomNavigation>

        {/* Mobile open orders content */}
        {showMobileOpenOrdersContent && (
          <MobileDashboard
            showMobileContent={() =>
              setShowMobileOpenOrdersContent(!showMobileOpenOrdersContent)
            }
          >
            <InfoCard
              title={t('screens:orders.openOrders')}
              content={filteredOrders.length || '0'}
            />
            <Divider />
            <PieChart
              values={ordersAmount}
              title={t('screens:orders.priorityOfOrders')}
              selectedData={(priority: any) => setSelectedPriority(priority)}
              showLegend={false}
            />
            <Divider />
            <CountOrdersChart
              title={t('screens:orders.ordersAging')}
              labels={agingLabels}
              data={agingSeries}
              type="aging"
              xAxisTitle={t('screens:orders.openTime')}
              yAxisTitle={t('screens:orders.numberOfOrders')}
              showTitle
            />
            <Divider />
            <CountOrdersChart
              showTitle
              title={t('screens:orders.historyGeneratedOfOrders')}
              labels={ordersHistoryLabels}
              data={ordersHistoryData}
              type="count"
              showDatePicker
              loading={loadingChart}
            />
            <Divider />
            {executionRateData.length > 0 && (
              <CountOrdersChart
                showTitle
                title={t('screens:orders.executionRate')}
                labels={executionRateLabels}
                data={executionRateData}
                type="count"
                // height={size.height && size.height * 0.215}
                showLegend
                yAxisTitle={t('screens:orders.numberOfOrders')}
                colors={[
                  theme.palette.primary.main,
                  theme.palette.primary.light,
                  '#CFD8DC',
                ]}
              />
            )}
            <Divider />
            <IncompleteOrdersContentMobile orders={filteredOrders} />
          </MobileDashboard>
        )}
      </Hidden>
      <ToastContainer
        style={{ position: 'absolute', top: 64, zIndex: 99999999999 }}
      />
    </div>
  );
};

export default Orders;
